// src/features/project-intake/ProjectIntakePage.tsx

import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { projectRequestsApi } from '../../services/api';
import { Customer } from '../../types';
import LoadingCard from '../../components/LoadingCard';
import { showSuccess } from '../../utils/toast';
import { useProjectIntakeData } from './hooks/useProjectIntakeData';
import { getFileTypeFromUrl, canSubmitForm, getInitialFormState, resetForm } from './utils/intakeHelpers';
import { ProjectIntakeForm, FormErrors, LightboxState } from './types';
import { CustomerSearchDropdown } from './components/CustomerSearchDropdown';
import { WorkRequestSelector } from './components/WorkRequestSelector';
import { FileUploadField } from './components/FileUploadField';
import { ConditionalFields } from './components/ConditionalFields';
import { Lightbox } from './components/Lightbox';

// Main project intake form page
const ProjectIntakePage: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<ProjectIntakeForm>(getInitialFormState());
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [requestEmailFile, setRequestEmailFile] = useState<File | null>(null);
  const [supportingDocsFile, setSupportingDocsFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<FormErrors>({ client: null, workType: null, workRequest: null, requestEmailFile: null });
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { customers, customerContacts, options, loading, error } = useProjectIntakeData(form.customer_id);

  // Calculate conditional field requirements
  const requiresResearch = useMemo(() => form.work_request.includes('Research'), [form.work_request]);
  const requiresAcquisition = useMemo(() => form.work_request.includes('Acquisition'), [form.work_request]);
  const requiresQaqc = useMemo(() => form.work_request.includes('QA/QC'), [form.work_request]);
  const requiresBasic = useMemo(() => form.work_request.includes('Basic Request'), [form.work_request]);

  // Check if form can be submitted
  const canSubmit = useMemo(
    () => canSubmitForm(form, requiresResearch, requiresBasic, requestEmailFile),
    [form, requiresResearch, requiresBasic, requestEmailFile]
  );

  // Handle basic form field changes
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as any;

    // Prevent negative values for impacted_parcels
    if (name === 'impacted_parcels' && type === 'number') {
      const numValue = Number(value);
      if (value !== '' && numValue < 0) return;
    }

    // Clear work type error when user selects a value
    if (name === 'work_type') {
      setErrors((prev) => ({ ...prev, workType: null }));
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Handle radio button changes
  const handleRadioChange = useCallback((name: string, value: string) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Handle work request toggle
  const handleWorkRequestToggle = useCallback((value: string) => {
    setForm((prev) => {
      const exists = prev.work_request.includes(value);
      const next = exists ? prev.work_request.filter((v) => v !== value) : [...prev.work_request, value];
      return { ...prev, work_request: next };
    });
    setErrors((prev) => ({ ...prev, workRequest: null }));
  }, []);

  // Handle customer selection
  const handleClientSelect = useCallback((customer: Customer) => {
    setForm((p) => ({
      ...p,
      customer_id: customer.id,
      funding_type: customer.funding_type || 'On-Call',
    }));
    setClientSearch(customer.item);
    setShowClientDropdown(false);
    setErrors((prev) => ({ ...prev, client: null }));
  }, []);

  // Handle client search change
  const handleClientSearchChange = useCallback((value: string) => {
    setClientSearch(value);
    setShowClientDropdown(true);
    if (!value) {
      setForm((p) => ({ ...p, customer_id: undefined }));
    }
    setErrors((prev) => ({ ...prev, client: null }));
  }, []);

  // Open lightbox
  const openLightbox = useCallback((url: string, type?: string) => {
    setLightbox({ url, type: (type as any) || getFileTypeFromUrl(url) });
  }, []);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    let hasError = false;
    const newErrors: FormErrors = { client: null, workType: null, workRequest: null, requestEmailFile: null };

    if (!form.customer_id && !form.customer_name) {
      newErrors.client = 'Please fill this field';
      hasError = true;
    }

    if (!form.work_type) {
      newErrors.workType = 'Please fill this field';
      hasError = true;
    }

    if (form.work_request.length === 0) {
      newErrors.workRequest = 'Please select at least one option';
      hasError = true;
    }

    if (!requestEmailFile) {
      newErrors.requestEmailFile = 'Please upload a request email file';
      hasError = true;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    if (!canSubmit || submitting) return;

    setSubmitting(true);
    setSubmitError(null);
    setErrors({ client: null, workType: null, workRequest: null, requestEmailFile: null });

    try {
      const response = await projectRequestsApi.create({
        customer_id: form.customer_id ?? null,
        customer_name: form.customer_name || null,
        project_name: form.project_name,
        funding_type: form.funding_type || null,
        work_type: form.work_type || null,
        work_request: form.work_request,
        impacted_parcels: form.impacted_parcels === '' ? null : Number(form.impacted_parcels),
        research_is_government_owned: requiresResearch ? form.research_is_government_owned === 'Yes' : null,
        acquisition_is_government_owned: requiresAcquisition ? form.acquisition_is_government_owned === 'Yes' : null,
        qa_has_documentation: requiresQaqc ? form.qa_has_documentation === 'Yes' : null,
        request_email_file: requestEmailFile || undefined,
        supporting_docs_file: supportingDocsFile || undefined,
        comments_notes: form.comments_notes || null,
      });

      // Reset form
      setForm(resetForm());
      setClientSearch('');
      setShowClientDropdown(false);
      setRequestEmailFile(null);
      setSupportingDocsFile(null);

      showSuccess('Project intake submitted successfully!');

      const newProjectRequestId = response.project_request?.id;
      navigate(`/project-requests?highlight=${newProjectRequestId}`);
    } catch (e: any) {
      setSubmitError(e?.response?.data?.message || e?.message || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingCard title="New Project Intake" message="Loading form data..." />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold theme-text-primary mb-2">New Project Intake</h1>
        <p className="text-sm theme-text-secondary">
          Fill out the form below to submit a new project request
        </p>
      </div>

      {(error || submitError) && (
        <div className="mb-4 p-4 rounded-lg bg-red-900/20 text-red-400 border border-red-500/20 text-sm flex items-start gap-2">
          <span className="text-lg">⚠️</span>
          <span>{error || submitError}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information Section */}
        <div className="theme-card rounded-lg border theme-border p-6">
          <h2 className="text-lg font-semibold theme-text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-8  rounded-lg bg-blue-600/20 flex items-center justify-center text-blue-500 font-bold">
              1
            </span>
            Basic Information
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <CustomerSearchDropdown
              customers={customers}
              customerContacts={customerContacts}
              searchValue={clientSearch}
              onSearchChange={handleClientSearchChange}
              onCustomerSelect={handleClientSelect}
              showDropdown={showClientDropdown}
              onFocus={() => setShowClientDropdown(true)}
              onBlur={() => setTimeout(() => setShowClientDropdown(false), 300)}
              error={errors.client}
            />
            <div>
              <label className="block text-sm font-medium theme-text-secondary mb-1.5">
                Project Name <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                name="project_name"
                value={form.project_name}
                onChange={handleChange}
                placeholder="Enter project name"
                className="w-full border theme-border rounded-lg px-3 py-2.5 text-sm theme-card theme-text-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium theme-text-secondary mb-1.5">Funding Type</label>
              <select
                name="funding_type"
                value={form.funding_type}
                onChange={handleChange}
                className="w-full border theme-border rounded-lg px-3 py-2.5 text-sm theme-card theme-text-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              >
                <option value="On-Call">On-Call</option>
                <option value="Project Management">Project Management</option>
                {options?.funding_types.filter((o) => o !== 'On-Call').map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium theme-text-secondary mb-1.5">
                Work Type <span className="text-red-500 ml-1">*</span>
              </label>
              <select
                name="work_type"
                value={form.work_type}
                onChange={handleChange}
                className={`w-full border rounded-lg px-3 py-2.5 text-sm theme-card theme-text-primary focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                  errors.workType ? 'border-red-500 ring-2 ring-red-900/20' : 'theme-border'
                }`}
              >
                <option value="">Select work type</option>
                {options?.work_types.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
              {errors.workType && (
                <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
                  <span>⚠</span> {errors.workType}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Work Request Section */}
        <div className="theme-card rounded-lg border theme-border p-6">
          <h2 className="text-lg font-semibold theme-text-primary mb-4 flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-purple-600/20 flex items-center justify-center text-purple-500 font-bold">
              2
            </span>
            Work Request
          </h2>
          <WorkRequestSelector
            options={options?.work_request_options || []}
            selectedValues={form.work_request}
            onToggle={handleWorkRequestToggle}
            error={errors.workRequest}
          />
        </div>

        {/* Conditional Fields */}
        <ConditionalFields
          requiresResearch={requiresResearch}
          requiresAcquisition={requiresAcquisition}
          requiresQaqc={requiresQaqc}
          impactedParcels={form.impacted_parcels}
          researchIsGovOwned={form.research_is_government_owned}
          acquisitionIsGovOwned={form.acquisition_is_government_owned}
          qaHasDocumentation={form.qa_has_documentation}
          onImpactedParcelsChange={(value) => setForm((prev) => ({ ...prev, impacted_parcels: value }))}
          onRadioChange={handleRadioChange}
        />

        {/* File Uploads */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FileUploadField
            label="Request Email File"
            file={requestEmailFile}
            onFileChange={(f) => {
              setRequestEmailFile(f);
              if (f) setErrors((prev) => ({ ...prev, requestEmailFile: null }));
            }}
            onPreviewClick={(url) => openLightbox(url)}
            required
            error={errors.requestEmailFile}
          />
          <FileUploadField
            label="Supporting Docs"
            file={supportingDocsFile}
            onFileChange={setSupportingDocsFile}
            onPreviewClick={(url) => openLightbox(url)}
          />
        </div>

        {/* Comments */}
        <div>
          <label className="text-sm font-semibold theme-text-primary">Comments/Notes</label>
          <textarea
            name="comments_notes"
            value={form.comments_notes}
            onChange={handleChange}
            className="w-full border theme-border rounded px-2 py-1 text-sm theme-card theme-text-primary min-h-[80px] focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => window.history.back()}
            className="px-3 py-1.5 rounded border theme-border theme-text-secondary hover:theme-bg-secondary text-sm font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit || submitting}
            className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 text-sm font-bold shadow-lg shadow-blue-900/20 transition-all"
          >
            {submitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </form>

      <Lightbox lightbox={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
};

export default ProjectIntakePage;
